import type { CustomFlowbiteTheme } from 'flowbite-react';

const customTheme: CustomFlowbiteTheme = {
	modal: {
		root: {
			// Replace: h-modal h-screen with h-full
			base: 'fixed top-0 right-0 left-0 z-50 h-full overflow-y-auto overflow-x-hidden md:inset-0 md:h-full align-start',
		},
		content: {
			// Added: flex flex-col justify-center
			base: 'relative w-full p-4 h-auto flex flex-col justify-center m-auto',
			// Added: max-h-full max-w-full overflow-hidden
			inner: 'relative rounded-lg bg-white shadow dark:bg-gray-700 flex flex-col',
		},
	},
	accordion: {
		root: {
			base: 'divide-y divide-gray-200 border-gray-200 dark:divide-gray-700 dark:border-gray-700',
			flush: {
				off: 'rounded-lg border',
				on: 'border-b',
			},
		},
		content: {
			base: 'py-5 px-5 last:rounded-b-lg dark:bg-inherit first:rounded-t-lg !border-0 !text-[15px] !pt-0 !font-inter text-[#C7C9D9]',
		},
		title: {
			arrow: {
				base: 'h-6 w-6 shrink-0',
				open: {
					off: '',
					on: 'rotate-180',
				},
			},
			base: 'flex w-full items-center justify-between first:rounded-t-lg last:rounded-b-lg py-5 text-left font-medium text-gray-500 dark:text-white text-[15px] font-inter !bg-transparent',
			flush: {
				off: 'hover:bg-gray-100 focus:ring-4 focus:ring-gray-200 dark:hover:bg-gray-800 dark:focus:ring-gray-800',
				on: 'bg-transparent dark:bg-transparent',
			},
			heading: '',
			open: {
				off: '',
				on: 'text-gray-900 bg-gray-100 dark:bg-gray-800 dark:text-white',
			},
		},
	},
	button: {
		base: 'group flex items-stretch items-center justify-center p-0.5 text-center font-medium relative focus:outline-none transition-[color,background-color,border-color,text-decoration-color,fill,stroke,box-shadow]',
		color: {
			dark: 'border border-transparent bg-gray-800 text-white focus:ring-4 focus:ring-gray-300 enabled:hover:bg-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:focus:ring-gray-800 dark:enabled:hover:bg-gray-700',
			failure:
				'border border-transparent bg-red-700 text-white focus:ring-4 focus:ring-red-300 enabled:hover:bg-red-800 dark:bg-red-600 dark:focus:ring-red-900 dark:enabled:hover:bg-red-700',
			gray: ':ring-purple-700 border border-gray-200 bg-white text-gray-900 focus:text-purple-700 focus:ring-4 enabled:hover:bg-gray-100 enabled:hover:text-purple-700 dark:border-gray-600 dark:bg-transparent dark:text-gray-400 dark:enabled:hover:bg-gray-700 dark:enabled:hover:text-white',
			info: 'border border-transparent bg-purple-700 text-white focus:ring-4 focus:ring-purple-300 enabled:hover:bg-purple-800 dark:bg-purple-600 dark:focus:ring-purple-800 dark:enabled:hover:bg-purple-700',
			light:
				'border border-gray-300 bg-white text-gray-900 focus:ring-4 focus:ring-purple-300 enabled:hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-600 dark:text-white dark:focus:ring-gray-700 dark:enabled:hover:border-gray-700 dark:enabled:hover:bg-gray-700',
			purple:
				'border border-transparent bg-purple-700 text-white focus:ring-4 focus:ring-purple-300 enabled:hover:bg-purple-800 dark:bg-purple-600 dark:focus:ring-purple-900 dark:enabled:hover:bg-purple-700',
			success:
				'border border-transparent bg-green-700 text-white focus:ring-4 focus:ring-green-300 enabled:hover:bg-green-800 dark:bg-green-600 dark:focus:ring-green-800 dark:enabled:hover:bg-green-700',
			warning:
				'border border-transparent bg-yellow-400 text-white focus:ring-4 focus:ring-yellow-300 enabled:hover:bg-yellow-500 dark:focus:ring-yellow-900',
			blue: 'border border-transparent bg-blue-700 text-white focus:ring-4 focus:ring-blue-300 enabled:hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800',
			cyan: 'border border-cyan-300 bg-white text-cyan-900 focus:ring-4 focus:ring-cyan-300 enabled:hover:bg-cyan-100 dark:border-cyan-600 dark:bg-cyan-600 dark:text-white dark:focus:ring-cyan-700 dark:enabled:hover:border-cyan-700 dark:enabled:hover:bg-cyan-700',
			green:
				'border border-green-300 bg-white text-green-900 focus:ring-4 focus:ring-green-300 enabled:hover:bg-green-100 dark:border-green-600 dark:bg-green-600 dark:text-white dark:focus:ring-green-700 dark:enabled:hover:border-green-700 dark:enabled:hover:bg-green-700',
			indigo:
				'border border-indigo-300 bg-white text-indigo-900 focus:ring-4 focus:ring-indigo-300 enabled:hover:bg-indigo-100 dark:border-indigo-600 dark:bg-indigo-600 dark:text-white dark:focus:ring-indigo-700 dark:enabled:hover:border-indigo-700 dark:enabled:hover:bg-indigo-700',
			lime: 'border border-lime-300 bg-white text-lime-900 focus:ring-4 focus:ring-lime-300 enabled:hover:bg-lime-100 dark:border-lime-600 dark:bg-lime-600 dark:text-white dark:focus:ring-lime-700 dark:enabled:hover:border-lime-700 dark:enabled:hover:bg-lime-700',
			pink: 'border border-pink-300 bg-white text-pink-900 focus:ring-4 focus:ring-pink-300 enabled:hover:bg-pink-100 dark:border-pink-600 dark:bg-pink-600 dark:text-white dark:focus:ring-pink-700 dark:enabled:hover:border-pink-700 dark:enabled:hover:bg-pink-700',
			red: 'border border-red-300 bg-white text-red-900 focus:ring-4 focus:ring-red-300 enabled:hover:bg-red-100 dark:border-red-600 dark:bg-red-600 dark:text-white dark:focus:ring-red-700 dark:enabled:hover:border-red-700 dark:enabled:hover:bg-red-700',
			teal: 'border border-teal-300 bg-white text-teal-900 focus:ring-4 focus:ring-teal-300 enabled:hover:bg-teal-100 dark:border-teal-600 dark:bg-teal-600 dark:text-white dark:focus:ring-teal-700 dark:enabled:hover:border-teal-700 dark:enabled:hover:bg-teal-700',
			yellow:
				'border border-yellow-300 bg-white text-yellow-900 focus:ring-4 focus:ring-yellow-300 enabled:hover:bg-yellow-100 dark:border-yellow-600 dark:bg-yellow-600 dark:text-white dark:focus:ring-yellow-700 dark:enabled:hover:border-yellow-700 dark:enabled:hover:bg-yellow-700',
			darkBlue:
				'border bg-gray-800 text-white focus:ring-4 focus:ring-gray-300 enabled:hover:bg-gray-900 border-[#5B8DEF] dark:bg-gray-800 dark:focus:ring-gray-800 dark:enabled:hover:bg-gray-700',
			whiteBlue:
				'border border-transparent bg-white text-[#7777E7] focus:ring-4 focus:ring-white-300 enabled:hover:bg-gray-200 enabled:active:bg-gray-300 dark:enabled:active:bg-gray-300 dark:hover:bg-gray-200 dark:focus:ring-white-800 font-[600]',
			whiteBlueOutline:
				'border border-[#5B8DEF] bg-white text-[#7777E7] focus:ring-4 focus:ring-white-300 enabled:hover:bg-gray-300 dark:hover:bg-gray-300 dark:focus:ring-white-800 font-[600]',
			purplePlaced:
				'border bg-[#7777E7] text-white focus:ring-4 focus:ring-gray-300 enabled:hover:bg-[#6666ed] border-[#7777E7]',
			purplePlacedOutline:
				'border bg-transparent text-[#7777E7] hover:text-white focus:ring-4 focus:ring-gray-300 enabled:hover:bg-[#6666ed] border-[#7777E7]',
		},
	},
	toggleSwitch: {
		toggle: {
			checked: {
				color: {
					blue: 'bg-[#7777E7] border-[#7777E7]',
				},
			},
		},
	},
	datepicker: {
		root: {
			base: 'relative [&_input]:bg-gray-50 [&_input]:border-gray-300',
		},
		popup: {
			root: {
				base: 'absolute top-10 z-50 block pt-2',
				inline: 'relative top-0 z-auto',
				inner: 'inline-block rounded-lg bg-white p-4 shadow-lg dark:bg-gray-700',
			},
			header: {
				base: '',
				title: 'px-2 py-3 text-center font-semibold text-gray-900 dark:text-white',
				selectors: {
					base: 'mb-2 flex justify-between',
					button: {
						base: 'rounded-lg bg-white px-5 py-2.5 text-sm font-semibold text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600',
						prev: '',
						next: '',
						view: '',
					},
				},
			},
			view: {
				base: 'p-1',
			},
			footer: {
				base: 'mt-2 flex space-x-2',
				button: {
					base: 'w-full rounded-lg px-5 py-2 text-center text-sm font-medium focus:ring-4 focus:ring-blue-300',
					today: 'bg-[#7777E7] text-white hover:bg-[#6666ed] dark:bg-[#7777E7] dark:hover:bg-[#6666ed]',
					clear:
						'border border-gray-300 bg-white text-gray-900 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600',
				},
			},
		},
		views: {
			days: {
				header: {
					base: 'mb-1 grid grid-cols-7',
					title: 'h-6 text-center text-sm font-medium leading-6 text-gray-500 dark:text-gray-400',
				},
				items: {
					base: 'grid w-64 grid-cols-7',
					item: {
						base: 'block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-600 ',
						selected: 'bg-[#7777E7] text-white hover:bg-[#6666ed]',
						disabled: 'text-gray-500',
					},
				},
			},
			months: {
				items: {
					base: 'grid w-64 grid-cols-4',
					item: {
						base: 'block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-600',
						selected: 'bg-[#7777E7] text-white hover:bg-[#6666ed]',
						disabled: 'text-gray-500',
					},
				},
			},
			years: {
				items: {
					base: 'grid w-64 grid-cols-4',
					item: {
						base: 'block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-600',
						selected: 'bg-[#7777E7] text-white hover:bg-[#6666ed]',
						disabled: 'text-gray-500',
					},
				},
			},
			decades: {
				items: {
					base: 'grid w-64 grid-cols-4',
					item: {
						base: 'block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9  text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-600',
						selected: 'bg-[#7777E7] text-white hover:bg-[#6666ed]',
						disabled: 'text-gray-500',
					},
				},
			},
		},
	},
	textInput: {
		base: 'flex',
		addon:
			'inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-200 px-3 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-600 dark:text-gray-400',
		field: {
			base: 'relative w-full',
			icon: {
				base: 'pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3',
				svg: 'h-5 w-5 text-gray-500 dark:text-gray-400',
			},
			rightIcon: {
				base: 'pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3',
				svg: 'h-5 w-5 text-gray-500 dark:text-gray-400',
			},
			input: {
				base: 'block w-full border disabled:cursor-not-allowed disabled:opacity-50',
				sizes: {
					sm: 'p-2 sm:text-xs',
					md: 'p-2.5 text-sm',
					lg: 'p-4 sm:text-base',
				},
				colors: {
					purple:
						'border-gray-300 bg-gray-50 text-gray-900 focus:border-[#7777E7] focus:ring-[#7777E7] dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-[#7777E7] dark:focus:ring-[#7777E7]',
					gray: 'border-gray-300 bg-gray-50 text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500',
					info: 'border-blue-500 bg-blue-50 text-blue-900 placeholder-blue-700 focus:border-blue-500 focus:ring-blue-500 dark:border-blue-400 dark:bg-blue-100 dark:focus:border-blue-500 dark:focus:ring-blue-500',
					failure:
						'border-red-500 bg-red-50 text-red-900 placeholder-red-700 focus:border-red-500 focus:ring-red-500 dark:border-red-400 dark:bg-red-100 dark:focus:border-red-500 dark:focus:ring-red-500',
					warning:
						'border-yellow-500 bg-yellow-50 text-yellow-900 placeholder-yellow-700 focus:border-yellow-500 focus:ring-yellow-500 dark:border-yellow-400 dark:bg-yellow-100 dark:focus:border-yellow-500 dark:focus:ring-yellow-500',
					success:
						'border-green-500 bg-green-50 text-green-900 placeholder-green-700 focus:border-green-500 focus:ring-green-500 dark:border-green-400 dark:bg-green-100 dark:focus:border-green-500 dark:focus:ring-green-500',
				},
				withRightIcon: {
					on: 'pr-10',
					off: '',
				},
				withIcon: {
					on: 'pl-10',
					off: '',
				},
				withAddon: {
					on: 'rounded-r-lg',
					off: 'rounded-lg',
				},
				withShadow: {
					on: 'shadow-sm dark:shadow-sm-light',
					off: '',
				},
			},
		},
	},
	select: {
		base: 'flex',
		addon:
			'inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-200 px-3 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-600 dark:text-gray-400',
		field: {
			base: 'relative w-full',
			icon: {
				base: 'pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3',
				svg: 'h-5 w-5 text-gray-500 dark:text-gray-400',
			},
			select: {
				base: 'block w-full border disabled:cursor-not-allowed disabled:opacity-50',
				withIcon: {
					on: 'pl-10',
					off: '',
				},
				withAddon: {
					on: 'rounded-r-lg',
					off: 'rounded-lg',
				},
				withShadow: {
					on: 'shadow-sm dark:shadow-sm-light',
					off: '',
				},
				sizes: {
					sm: 'p-2 sm:text-xs',
					md: 'p-2.5 text-sm',
					lg: 'p-4 sm:text-base',
				},
				colors: {
					gray: 'border-gray-300 bg-gray-50 text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500',
					info: 'border-blue-500 bg-blue-50 text-blue-900 placeholder-blue-700 focus:border-blue-500 focus:ring-blue-500 dark:border-blue-400 dark:bg-blue-100 dark:focus:border-blue-500 dark:focus:ring-blue-500',
					failure:
						'border-red-500 bg-red-50 text-red-900 placeholder-red-700 focus:border-red-500 focus:ring-red-500 dark:border-red-400 dark:bg-red-100 dark:focus:border-red-500 dark:focus:ring-red-500',
					warning:
						'border-yellow-500 bg-yellow-50 text-yellow-900 placeholder-yellow-700 focus:border-yellow-500 focus:ring-yellow-500 dark:border-yellow-400 dark:bg-yellow-100 dark:focus:border-yellow-500 dark:focus:ring-yellow-500',
					success:
						'border-green-500 bg-green-50 text-green-900 placeholder-green-700 focus:border-green-500 focus:ring-green-500 dark:border-green-400 dark:bg-green-100 dark:focus:border-green-500 dark:focus:ring-green-500',
				},
			},
		},
	},
	radio: {
		root: {
			base: 'h-4 w-4 border border-gray-300 text-[#7777E7] focus:ring-2 focus:ring-[#7777E7] dark:border-gray-600 dark:bg-gray-700 dark:focus:bg-[#7777E7] dark:focus:ring-[#7777E7]',
		},
	},
	fileInput: {
		root: {
			base: 'flex',
		},
		field: {
			base: 'relative w-full',
			input: {
				base: 'block w-full overflow-hidden rounded-lg border disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#7777E7]',
				sizes: {
					sm: 'sm:text-xs',
					md: 'text-sm',
					lg: 'sm:text-base',
				},
				colors: {
					gray: 'border-gray-300 bg-gray-50 text-gray-900 focus:border-[#7777E7] focus:ring-[#7777E7] dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-[#7777E7] dark:focus:ring-[#7777E7]',
					info: 'border-[#7777E7] bg-purple-50 text-purple-900 placeholder-purple-700 focus:border-[#7777E7] focus:ring-[#7777E7] dark:border-purple-400 dark:bg-purple-100 dark:focus:border-[#7777E7] dark:focus:ring-[#7777E7]',
					failure:
						'border-red-500 bg-red-50 text-red-900 placeholder-red-700 focus:border-red-500 focus:ring-red-500 dark:border-red-400 dark:bg-red-100 dark:focus:border-red-500 dark:focus:ring-red-500',
					warning:
						'border-yellow-500 bg-yellow-50 text-yellow-900 placeholder-yellow-700 focus:border-yellow-500 focus:ring-yellow-500 dark:border-yellow-400 dark:bg-yellow-100 dark:focus:border-yellow-500 dark:focus:ring-yellow-500',
					success:
						'border-green-500 bg-green-50 text-green-900 placeholder-green-700 focus:border-green-500 focus:ring-green-500 dark:border-green-400 dark:bg-green-100 dark:focus:border-green-500 dark:focus:ring-green-500',
				},
			},
		},
	},
};

export default customTheme;
